'use client';

import { ChangeEventHandler, MouseEventHandler, useCallback } from 'react';

import { Styles, css, cx } from '@flipster/styled-system/css';

export interface SliderProps {
  css?: Styles;
  name?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  marks: number[];
  onChange?: (value: number) => void;
  getLabel?: (value: number) => string;
}

export const Slider = ({
  css: cssProps,
  name,
  min = 0,
  max = 100,
  step = 1,
  value = 0,
  marks,
  onChange = () => {},
  getLabel = String,
}: SliderProps) => {
  const onInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => onChange(Number(e.currentTarget.value)),
    [onChange],
  );
  const onLabelClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => onChange(Number(e.currentTarget.value)),
    [onChange],
  );
  return (
    <div className={css(outerContainerStyle, cssProps)}>
      <div className={innerContainerStyle}>
        <div className={barStyle}>
          <div className={barFillStyle} style={{ width: getPercent(min, max, value) }} />
          <div className={cx(marksContainerStyle, css({ h: markSize }))}>
            {marks.map((mark) => (
              <Mark key={mark} percent={getPercent(min, max, mark)} on={mark <= value} />
            ))}
          </div>
          <div className={cx(marksContainerStyle, css({ top: 16 }))}>
            {marks.map((mark) => (
              <Label
                key={mark}
                value={mark}
                label={getLabel(mark)}
                percent={getPercent(min, max, mark)}
                onClick={onLabelClick}
              />
            ))}
          </div>
        </div>
        <input
          type='range'
          name={name}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onInputChange}
          tabIndex={-1}
          className={inputStyle}
        />
      </div>
    </div>
  );
};

const barHeight = 4;
const markSize = 10;
const handleSize = 18;
const handleDiff = handleSize - markSize;

const outerContainerStyle = css.raw({
  pos: 'relative',
  width: '100%',
  h: markSize + 26,
});

const innerContainerStyle = css({
  pos: 'absolute',
  top: -handleDiff / 2,
  left: -handleDiff / 2,
  w: `calc(100% + ${handleDiff}px)`,
});

const barStyle = css({
  pos: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  h: barHeight,
  mx: handleSize / 2,
  my: (handleSize - barHeight) / 2,
  touchAction: 'none',
  bgColor: 'border.default.level-3',
});
const barFillStyle = css({
  pos: 'absolute',
  left: 0,
  height: '100%',
  bgColor: 'content.accent.info',
});

const marksContainerStyle = css({
  pos: 'absolute',
  left: 0,
  w: '100%',
});

const inputStyle = css({
  pos: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  appearance: 'none',
  bgColor: 'transparent',
  outline: 'none',
  _focus: { outline: 'none' },
  '&::-webkit-slider-thumb': {
    w: handleSize,
    h: handleSize,
    appearance: 'none',
    cursor: 'grab',
    bgColor: 'content.accent.info',
    rounded: handleSize,
  },
  '&::-webkit-slider-runnable-track': {
    w: '100%',
    h: '100%',
  },
});

interface MarkProps {
  percent: string;
  on: boolean;
}
const Mark = ({ percent, on }: MarkProps) => {
  return <div className={cx(markStyle, on ? markOnStyle : markOffStyle)} style={{ left: percent }} />;
};
const markStyle = css({
  position: 'absolute',
  top: -barHeight / 2,
  w: markSize,
  h: markSize,
  rounded: markSize,
  borderWidth: '1px',
  borderStyle: 'solid',
  transform: 'translateX(-50%)',
});
const markOnStyle = css({
  bgColor: '{colors.blue.300}',
  borderColor: 'content.accent.info',
});
const markOffStyle = css({
  bgColor: 'background.default.level-4',
  borderColor: 'border.default.level-3',
});

interface LabelProps {
  value: number;
  label: string;
  percent: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
}
const Label = ({ value, label, percent, onClick }: LabelProps) => {
  return (
    <button type='button' className={labelStyle} style={{ left: percent }} value={value} onClick={onClick}>
      {label}
    </button>
  );
};
const labelStyle = css({
  position: 'absolute',
  top: 0,
  textStyle: 'Text/M/Regular',
  color: 'content.default.level-5',
  transform: 'translateX(-50%)',
  '&:first-child': { transform: 'none', left: `${-markSize / 2}px!` },
  '&:last-child': { transform: 'none', left: 'initial!', right: -markSize / 2 },
});

function getPercent(min: number, max: number, value: number): string {
  return `${normalize(min, max, value) * 100}%`;
}

// normalize(0, 100, 50) => 0.5
function normalize(start: number, end: number, value: number): number {
  if (end === start) return 0;
  return (value - start) / (end - start); // 0 ~ 1
}
